import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import { PencilFill, CheckLg, XLg, PlusCircle, Trash } from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/SecurityContext';
import RoleGuard from '../security/RoleGuard';
import SectionControls from '../SectionControls';
import { updateTrackPartnersSection } from '../../services/contentService';
import ImageUrlField from './ImageUrlField';
import './TrackPartnersBlock.scss';

function defaultPartner() {
  return { name: 'Partner name', logoUrl: '', websiteUrl: '' };
}

export function defaultTrackPartnersData() {
  return {
    title: 'Track partners',
    intro: 'These organizations are collaborating on this challenge.',
    partners: [defaultPartner(), defaultPartner()],
  };
}

export function TrackPartnersView({ data }) {
  const partners = Array.isArray(data?.partners) ? data.partners : [];
  return (
    <div className="track-partners-block">
      {data?.title ? <h2 className="track-partners-block__title">{data.title}</h2> : null}
      {data?.intro ? <p className="track-partners-block__intro">{data.intro}</p> : null}
      <Row className="track-partners-block__cards g-3 justify-content-center">
        {partners.map((p, idx) => {
          const inner = (
            <div className="track-partners-block__card">
              <div className="track-partners-block__logo-wrap">
                {p.logoUrl ? (
                  <img src={p.logoUrl} alt={p.name || ''} className="track-partners-block__logo" />
                ) : (
                  <div className="track-partners-block__logo-placeholder">{p.name || '?'}</div>
                )}
              </div>
              <div className="track-partners-block__name">{p.name}</div>
            </div>
          );
          return (
            <Col key={idx} xs={6} md={4} lg={3} className="track-partners-block__col">
              {p.websiteUrl ? (
                <a
                  href={p.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="track-partners-block__link"
                >
                  {inner}
                </a>
              ) : (
                inner
              )}
            </Col>
          );
        })}
      </Row>
    </div>
  );
}

function TrackPartnersBlock({
  pageId, section, onMoveUp, onMoveDown, onDelete, isFirstSection, isLastSection,
}) {
  const { user } = useAuth();
  const initialData = useMemo(() => section?.data || defaultTrackPartnersData(), [section]);
  const [data, setData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => { setData(section?.data || defaultTrackPartnersData()); }, [section]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      await updateTrackPartnersSection(pageId, section.position, data, modifiedBy);
      setIsEditing(false);
    } catch (e) {
      console.error('Error saving track partners:', e);
      // eslint-disable-next-line no-alert
      alert('Failed to save section.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() { setData(section?.data || defaultTrackPartnersData()); setIsEditing(false); }
  function updateField(field, value) { setData((prev) => ({ ...prev, [field]: value })); }
  function updatePartner(idx, patch) {
    setData((prev) => {
      const next = { ...prev, partners: [...(prev.partners || [])] };
      next.partners[idx] = { ...next.partners[idx], ...patch };
      return next;
    });
  }
  function addPartner() { setData((prev) => ({ ...prev, partners: [...(prev.partners || []), defaultPartner()] })); }
  function removePartner(idx) {
    setData((prev) => {
      const next = { ...prev, partners: [...(prev.partners || [])] };
      next.partners.splice(idx, 1);
      return next;
    });
  }

  if (isEditing) {
    return (
      <div className="track-partners-block track-partners-block--editing">
        <Form.Group className="mb-3">
          <Form.Label>Section title</Form.Label>
          <Form.Control type="text" value={data.title || ''} onChange={(e) => updateField('title', e.target.value)} />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Intro paragraph (optional)</Form.Label>
          <Form.Control as="textarea" rows={2} value={data.intro || ''} onChange={(e) => updateField('intro', e.target.value)} />
        </Form.Group>

        {(data.partners || []).map((p, idx) => (
          <div key={idx} className="track-partners-block__editor-partner">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Partner {idx + 1}</strong>
              <Button variant="outline-danger" size="sm" onClick={() => removePartner(idx)}>
                <Trash /> Remove
              </Button>
            </div>
            <Row className="g-2">
              <Col md={6}>
                <Form.Label>Name</Form.Label>
                <Form.Control type="text" value={p.name || ''} onChange={(e) => updatePartner(idx, { name: e.target.value })} />
              </Col>
              <Col md={6}>
                <Form.Label>Website URL (opens in new tab)</Form.Label>
                <Form.Control type="text" placeholder="https://…" value={p.websiteUrl || ''} onChange={(e) => updatePartner(idx, { websiteUrl: e.target.value })} />
              </Col>
              <Col md={12}>
                <ImageUrlField
                  label="Logo"
                  value={p.logoUrl}
                  onChange={(url) => updatePartner(idx, { logoUrl: url })}
                />
              </Col>
            </Row>
          </div>
        ))}

        <Button variant="outline-primary" size="sm" onClick={addPartner} className="mb-3">
          <PlusCircle /> Add partner
        </Button>

        <div className="track-partners-block__editor-actions">
          <Button variant="success" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (<><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>) : (<><CheckLg /> Save</>)}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}><XLg /> Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="track-partners-block-wrapper">
      <TrackPartnersView data={data} />
      <RoleGuard role="admin">
        <div className="admin-controls">
          <Button variant="outline-secondary" size="sm" className="edit-btn" onClick={() => setIsEditing(true)}>
            <PencilFill /> Edit partners
          </Button>
          <SectionControls onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDelete={onDelete} isFirstSection={isFirstSection} isLastSection={isLastSection} />
        </div>
      </RoleGuard>
    </div>
  );
}

export default TrackPartnersBlock;
